import { getQueryParams, getQueryParam } from 'utils/query'
import { isValidProductName, getProductQueryParam } from 'utils/utils'
import { CLIENT_ROUTE_PATHS } from 'utils/routes'
import { 
  FLOWS,
  FLOWS_SUPPORTED_PRODUCTS,
  MANAGEMENT_ACTION_TABS,
  MANAGE_CARD_TOUCHPOINTS,
  getManagementRouteUrl
} from 'universal'
import { getProductSystemNameByAlias } from 'universal/cfs-card'
import { EVALUATE_FUNDING_OPTIONS_ENTRY_POINTS } from 'universal/entry-points'
import { get } from 'lodash-es'
import { safeParseJSONInCollection } from 'utils/formatter'
import {
  hasFeature,
  hasUCPFeature,
  serverData,
  getCdnAssetUrl,
  getManageCardActions,
} from 'utils/server-data'
import { isRequestPhysicalCardWithFeeAllowed,shouldSetPin } from 'utils/card'
import { MANAGE_CARD_ACTIONS } from '@/constants/card'
import MANAGE_CARD_TAGS from '@/constants/fpti-tags/manage-card'
import qs from 'query-string'
import { goTo, redirectTo, getShowCardFlowUrl } from 'utils/navigate'
import { createChangeAddressFlowUrl, createEditFundingFlowUrl,createSetPinFlowUrl } from 'utils/flow'
import { ADDRESS_TYPES } from 'universal/address'
import { isCardDeactivatedOrLocked } from 'universal/lock'
import * as manageActions from './manage-actions'
import { INTENT_TYPES } from 'universal/constants'
import { getCardManagementPath } from 'universal/routes'
import { isFlowSupported } from 'universal/ucs'
const {
  lockOrUnlockCardImpressionTags,
  changePinImpressionTags,
  changePinClickTags,
  addCashImpressionTags,
  addCashClickTags,
  findAtmImpressionTags,
  findAtmClickTags,
  replaceCardImpressionTags,
  replaceCardClickTags,
  replaceDigitalCardImpressionTags,
  replaceDigitalCardClickTags,
  getPhysicalCardImpressionTags,
  getPhysicalCardClickTags,
  getPhysicalCardWithFeeClickTags,
  getPhysicalCardWithFeeImpressionTags,
  cancelCardImpressionTags,
  cancelCardClickTags,
  viewPinImpressionTags,
  viewPinClickTags,
} = MANAGE_CARD_TAGS

const {
  LOCK_OR_UNLOCK_CARD,
  VIEW_PIN,
  CHANGE_PIN,
  ADD_CASH,
  FIND_ATM,
  REPORT_LOST_OR_DAMAGED,
  CANCEL_CARD,
  GET_PHYSICAL_CARD,
  REPORT_LOST_OR_DAMAGED_DIGITAL,
  CREATE_PIN,
} = MANAGE_CARD_ACTIONS

const { MANAGE_CARD } = FLOWS

export const getFlowInputParams = () => {
  const { productName } = getQueryParams()
  return {
    productName: isValidProductName(MANAGE_CARD, productName)
      ? getProductSystemNameByAlias(productName)
      : null,
  }
}

export const getEmptyActivityImageUrl = () =>
  getCdnAssetUrl('paypal-ui/illustrations/focused/svg/empty-card-activity.svg')

export const initActivityWidget = (activityWidget) => {
  const { widgetCss = [], widgetJs = [] } = activityWidget

  widgetCss.forEach((cssLink) => {
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.href = cssLink
    document.head.appendChild(link)
  })

  widgetJs.forEach((src) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    document.body.appendChild(script)
  })
}

export const parseWidgetResponse = (widgets = []) => {
  const { CARD_BASIC_WIDGET, ACTION_WIDGET, NOTIFICATION_WIDGET } = MANAGE_CARD_TOUCHPOINTS
  const parsedWidgetInfo = {}
  widgets.forEach((widget) => {
    const widgetInfo = get(widget, 'messages[0].content.json')
    safeParseJSONInCollection(widgetInfo)
    switch (widget.touchpointCode) {
      case CARD_BASIC_WIDGET:
        parsedWidgetInfo.cardWidget = widgetInfo
        break
      case ACTION_WIDGET:
        parsedWidgetInfo.actionWidget = widgetInfo
        break
      case NOTIFICATION_WIDGET:
        parsedWidgetInfo.notificationWidgets = widget.messages
        break
      default:
        return
    }
  })
  return parsedWidgetInfo
}

export const isManageCardAllowed = (productName, status) => {
  return (
    productName &&
    hasFeature('enableManageCard') &&
    FLOWS_SUPPORTED_PRODUCTS[MANAGE_CARD].includes(productName) &&
    !(!hasUCPFeature('enableViewDeactivatedLockedCardDetails') && isCardDeactivatedOrLocked(status))
  )
}

export const isRewardsExperienceEnabled = () => hasFeature('enableRewardsExperience')

/**
 * @param {card} Card object containing card details fetched from cards/search api
 * @param {actionType} Possible action types defined in MANAGE_CARD_ACTIONS
 * @return An object which contains the action item details to render. Attributes includes:
 * - icon: JSX Icon to render (required)
 * - titleContentKey: Title of action item (required)
 * - descContentKey: Description of action item
 * - cta: Call to action JSX element (e.g., Switch, Button)
 * - actionPath: Redirects to actionPath on click
 *
 */
// eslint-disable-next-line complexity
export const getActionItemDetails = (card, action) => {
  const { changePinUrl, replaceCardUrl, requestPhysicalCardUrl, cancelCardUrl, viewPinUrl, createPinURL } =
    getManagementRouteUrl(card)
  const { showAddCash = false, showFindAtm = false, showCreatePin = false  } = getManageCardActions()
  const { actionUrls = {} } = card.metadata || {}
  switch (action.operation) {
    case LOCK_OR_UNLOCK_CARD:
      return manageActions.getLockOrUnlockCardAction({ card })
    case VIEW_PIN:
      return manageActions.getViewPinAction({ card, viewPinUrl })
    case CHANGE_PIN:
      return manageActions.getChangePinAction({ action, card, changePinUrl, showCreatePin })
    case CREATE_PIN:
      return manageActions.getCreatePinAction({ action, card, createPinURL, showCreatePin })
    case ADD_CASH:
      return manageActions.getAddCashAction({ actionUrls, showAddCash, card })
    case FIND_ATM:
      return manageActions.getFindAtmAction({ actionUrls, card, showFindAtm })
    case REPORT_LOST_OR_DAMAGED:
      return manageActions.getReportLostOrDamagedAction({ card, replaceCardUrl })
    case REPORT_LOST_OR_DAMAGED_DIGITAL:
      return manageActions.getLostOrDamagedDigitalAction({ card, replaceCardUrl })
    case GET_PHYSICAL_CARD:
      return manageActions.getPhysicalCardAction({ card, replaceCardUrl, requestPhysicalCardUrl })
    case CANCEL_CARD:
      return manageActions.getCancelCardAction({ card, cancelCardUrl })
    default:
      return false
  }
}

export const getActionItemFptiPayload = (card, actionType) => {
  switch (actionType) {
    case LOCK_OR_UNLOCK_CARD:
      return {
        im: lockOrUnlockCardImpressionTags, 
        // cl tag is handled in LockSwitch 
      }
    case CHANGE_PIN:
      return {
        im: changePinImpressionTags, 
        cl: changePinClickTags
      }
    case ADD_CASH:
      return {
        im: addCashImpressionTags,
        cl: addCashClickTags
      }
    case FIND_ATM:
      return {
        im: findAtmImpressionTags,
        cl: findAtmClickTags
      }
    case REPORT_LOST_OR_DAMAGED:
      return {
        im: replaceCardImpressionTags, 
        cl: replaceCardClickTags
      }
    case REPORT_LOST_OR_DAMAGED_DIGITAL:
      return {
        im: replaceDigitalCardImpressionTags,
        cl: replaceDigitalCardClickTags
      }
    case GET_PHYSICAL_CARD:
      const convertWithFee = isRequestPhysicalCardWithFeeAllowed(card, FLOWS.MANAGE_CARD)
      return {
        im: convertWithFee ? getPhysicalCardWithFeeImpressionTags : getPhysicalCardImpressionTags,
        cl: convertWithFee ? getPhysicalCardWithFeeClickTags : getPhysicalCardClickTags
      }
    case CANCEL_CARD:
      return {
        im: cancelCardImpressionTags,
        cl: cancelCardClickTags
      }
    case VIEW_PIN:
      return {
        im: viewPinImpressionTags,
        cl: viewPinClickTags,
      }
    default:
      return {}
  }
}

export const goToUCSRewards = () => {
  goTo(CLIENT_ROUTE_PATHS.REWARDS)
}

export const goToChangeBillingAddressFlow = (
  card,
  additionalReturnUriQueryParameters = {},
  options,
) => {
  if (!card) return

  const { productName = '', cardId = '' } = card

  if (!productName && !cardId) return

  const returnUriQueryString = qs.stringify({
    productName: getProductQueryParam(productName),
    ...qs.parse(window.location.search),
    ...additionalReturnUriQueryParameters,
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const changeAddressFlowUrl = createChangeAddressFlowUrl({
    productName,
    cardId,
    addressType: ADDRESS_TYPES.BILLING,
    returnUri,
  })

  if (!changeAddressFlowUrl) {
    return
  }

  const basePath = serverData.requestURI
  redirectTo(`${basePath}${changeAddressFlowUrl}`, options)
}

export const goToEditFundingFlow = ({
  cardId,
  additionalReturnUriQueryParameters = {},
  queryParams = {},
  entryPoint = EVALUATE_FUNDING_OPTIONS_ENTRY_POINTS.CHANGE_FI,
  options,
}) => {
  if (!cardId) return

  const returnUriQueryString = qs.stringify({
    ...qs.parse(window.location.search),
    ...additionalReturnUriQueryParameters,
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const editFundingFlowUrl = createEditFundingFlowUrl({
    cardId,
    returnUri,
    queryParams,
    entryPoint,
  })

  if (!editFundingFlowUrl) {
    return
  }

  const basePath = serverData.requestURI
  redirectTo(`${basePath}${editFundingFlowUrl}`, options)
}

export const goToSetPinFlow = (card, additionalQueryParams) => {
  if (!card) return

  const { productName = '' } = card

  const additionalParams = {
    productName,
    hidePinSuccess: true,
    ...additionalQueryParams,
  }

  const setPinFlowUrl = createSetPinFlowUrl(card, additionalParams)

  if (!setPinFlowUrl) {
    return
  }

  const basePath = serverData.requestURI
  redirectTo(`${basePath}${setPinFlowUrl}`)
}

export const redirectToShowCardFlow = (card, options) => {
  const showCardFlowUrl = getShowCardFlowUrl(card)
 const { productName } = card
  if (!showCardFlowUrl) {
    return
  }

  if(isFlowSupported(FLOWS.SET_PIN, productName) && hasFeature('enableSetPin') && shouldSetPin(card, { shouldBeSetByUser: true, shouldCheckOfflinePin: false })) {
    const cancelUri =  getCardManagementPath({ productName })
    const returnUri = cancelUri
    goToSetPinFlow(card, { intent: INTENT_TYPES.SHOW_CARD, cancelUri, returnUri });
  }else{
    const basePath = serverData.requestURI
    redirectTo(`${basePath}${showCardFlowUrl}`, options)
  }

  
}

export const getActionTabIndex = () => {
  const tab = getQueryParam('selectedTab')
  const SUMMARY_TAB_INDEXES = {
    MANAGE_TAB_INDEX: 0,
    ACTIVITY_TAB_INDEX: 1,
  }
  const { ACTIVITY, MANAGE } = MANAGEMENT_ACTION_TABS
  const { ACTIVITY_TAB_INDEX, MANAGE_TAB_INDEX } = SUMMARY_TAB_INDEXES

  switch (tab) {
    case ACTIVITY:
      return ACTIVITY_TAB_INDEX
    case MANAGE:
    default:
      return MANAGE_TAB_INDEX
  }
}
